import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

export default function Footer() {
  const { t, i18n } = useTranslation();
  const isAr = i18n.language === 'ar';

  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const serviceLinks = [
    { label: t('bugFixing'), path: '/bug-fixing', icon: 'ri-bug-line' },
    { label: t('customDev'), path: '/custom-development', icon: 'ri-code-s-slash-line' },
    { label: t('cloudServices'), path: '/cloud-services', icon: 'ri-cloud-line' },
    { label: t('workplaceAuto'), path: '/workplace-automation', icon: 'ri-settings-3-line' },
    { label: t('perfOptimization'), path: '/performance-optimization', icon: 'ri-speed-line' },
  ];

  return (
    <footer className="bg-gradient-to-br from-gray-900 to-gray-800 text-white pt-16 pb-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 mb-12">
          <div>
            <div className="flex items-center mb-4">
              <img
                src="https://static.readdy.ai/image/c83f6350fa782ce29b612b38d2cee1d4/3b7550d6c06eea93242f62b6efa2ff71.png"
                alt="Debug Logo"
                title="Debug - Software Development Company"
                className="h-10 w-auto"
              />
            </div>
            <p className="text-gray-400 text-sm leading-relaxed mb-4">
              {t('footerDescription')}
            </p>
            <div className="flex gap-4">
              <a href="https://www.facebook.com/debugsoftware/" target="_blank" rel="noopener noreferrer nofollow" className="w-10 h-10 bg-gray-800 rounded-lg flex items-center justify-center hover:bg-[rgb(0,0,139)] transition-colors cursor-pointer" aria-label="Debug on Facebook">
                <i className="ri-facebook-line text-lg"></i>
              </a>
              <a href="https://www.instagram.com/debugsoftware1/" target="_blank" rel="noopener noreferrer nofollow" className="w-10 h-10 bg-gray-800 rounded-lg flex items-center justify-center hover:bg-[rgb(0,0,139)] transition-colors cursor-pointer" aria-label="Debug on Instagram">
                <i className="ri-instagram-line text-lg"></i>
              </a>
              <a href="https://www.linkedin.com/company/debug-software" target="_blank" rel="noopener noreferrer nofollow" className="w-10 h-10 bg-gray-800 rounded-lg flex items-center justify-center hover:bg-[rgb(0,0,139)] transition-colors cursor-pointer" aria-label="Debug on LinkedIn">
                <i className="ri-linkedin-line text-lg"></i>
              </a>
            </div>
          </div>

          <div>
            <h4 className="text-lg font-bold mb-4">{t('servicesFooter')}</h4>
            <ul className="space-y-3 text-sm">
              {serviceLinks.map((service) => (
                <li key={service.path}>
                  <Link to={service.path} className="text-gray-400 hover:text-white transition-colors cursor-pointer flex items-center group">
                    <div className="w-10 h-10 bg-gray-800 rounded-lg flex items-center justify-center mr-3 rtl:ml-3 rtl:mr-0 group-hover:bg-[rgb(0,0,139)] transition-colors">
                      <i className={`${service.icon} text-lg text-white`}></i>
                    </div>
                    {service.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h4 className="text-lg font-bold mb-4">{t('company')}</h4>
            <ul className="space-y-3 text-sm">
              <li>
                <button onClick={() => scrollToSection('team')} className="text-gray-400 hover:text-[rgb(0,0,139)] transition-colors cursor-pointer">
                  {t('aboutUs')}
                </button>
              </li>
              <li>
                <button onClick={() => scrollToSection('projects')} className="text-gray-400 hover:text-[rgb(0,0,139)] transition-colors cursor-pointer">
                  {t('ourWorkFooter')}
                </button>
              </li>
              <li>
                <Link to="/blog" className="text-gray-400 hover:text-[rgb(0,0,139)] transition-colors cursor-pointer">
                  {isAr ? 'المدونة' : 'Blog'}
                </Link>
              </li>
              <li>
                <button onClick={() => scrollToSection('faq')} className="text-gray-400 hover:text-[rgb(0,0,139)] transition-colors cursor-pointer">
                  {t('faqFooter')}
                </button>
              </li>
              <li>
                <button onClick={() => scrollToSection('contact')} className="text-gray-400 hover:text-[rgb(0,0,139)] transition-colors cursor-pointer">
                  {t('contactFooter')}
                </button>
              </li>
            </ul>
          </div>

          <div>
            <h4 className="text-lg font-bold mb-4">{t('contactInfo')}</h4>
            <ul className="space-y-3 text-sm text-gray-400">
              <li className="flex items-start">
                <div className="w-10 h-10 bg-gray-800 rounded-lg flex items-center justify-center mr-3 rtl:ml-3 rtl:mr-0 flex-shrink-0 hover:bg-[rgb(0,0,139)] transition-colors">
                  <i className="ri-mail-line text-white text-lg"></i>
                </div>
                <span className="mt-1">contact@debuging.com</span>
              </li>
              <li className="flex items-start">
                <div className="w-10 h-10 bg-gray-800 rounded-lg flex items-center justify-center mr-3 rtl:ml-3 rtl:mr-0 flex-shrink-0 hover:bg-[rgb(0,0,139)] transition-colors">
                  <i className="ri-phone-line text-white text-lg"></i>
                </div>
                <span className="mt-1">+20 109 9289 280</span>
              </li>
              <li className="flex items-start">
                <div className="w-10 h-10 bg-gray-800 rounded-lg flex items-center justify-center mr-3 rtl:ml-3 rtl:mr-0 flex-shrink-0 hover:bg-[rgb(0,0,139)] transition-colors">
                  <i className="ri-customer-service-2-line text-white text-lg"></i>
                </div>
                <span className="mt-1">{t('aiSupport')}</span>
              </li>
            </ul>
          </div>
        </div>

        <div className="border-t border-gray-700 pt-8">
          <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
            <p className="text-sm text-gray-400">
              {t('copyright')}
            </p>
            <div className="flex gap-6 text-sm">
              <Link to="/privacy" className="text-gray-400 hover:text-[rgb(0,0,139)] transition-colors cursor-pointer">
                {t("privacyPolicy")}
              </Link>
              <Link to="/terms-of-service" className="text-gray-400 hover:text-[rgb(0,0,139)] transition-colors cursor-pointer">
                {t("termsOfService")}
              </Link>
              <span className="text-gray-400">
                {t('poweredBy')}
              </span>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}